import { useEffect, useState } from 'react';
import { getType } from '../../../../sources/type';
import type { OperationType } from '../../../../types/graph';
import type { TypeElementBindingAPI } from '../../../../types/type';
import FormGroup from '../../../generic/FormGroup';
import Loader from '../../../loader/Loader';
import { DATA_ACTION_TYPE_BINDING_LABEL } from './constants';

type Props = {
    typeElementId: string | null | undefined;
    typeElementBindingId: string;
    onSelect: (id: string) => void;
    validationMessage: string;
    isValid: boolean;
    showValidation: boolean;
    crudOperationType: OperationType;
};

const TypeBindingDropdown = ({
    typeElementId,
    typeElementBindingId,
    onSelect,
    validationMessage,
    isValid,
    showValidation,
    crudOperationType,
}: Props) => {
    const [bindings, setBindings] = useState<TypeElementBindingAPI[]>([]);
    const [isServiceElement, setIsServiceElement] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState(true);

    const fetchType = async () => {
        if (typeElementId) {
            const typeResult = await getType(typeElementId);

            if (typeResult.bindings) {
                setBindings(typeResult.bindings);

                // On render typeElementBindingId is empty so client side validation thinks nothing is selected.
                // Since the first option is automatically selected it's impossible to get onSelect to call unless another value exists or it's done manually like this.
                if (typeElementBindingId === '' && typeResult.bindings.length > 0) {
                    onSelect(typeResult.bindings[0].id);
                }
            } else {
                setBindings([]);
            }

            setIsServiceElement(!!typeResult.serviceElementId);
            setIsLoading(false);
        }
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        if (typeElementId) {
            fetchType();
        } else {
            setBindings([]);
            setIsServiceElement(false);
            setIsLoading(false);
        }
    }, [typeElementId]);

    if (isLoading) {
        return <Loader />;
    }

    if (!isServiceElement) {
        return null;
    }

    return (
        <FormGroup
            label={DATA_ACTION_TYPE_BINDING_LABEL[crudOperationType]}
            htmlFor="type-bindings"
            isRequired
            validationMessage={validationMessage}
            isValid={isValid}
            showValidation={showValidation}
        >
            <select
                id="type-bindings"
                value={typeElementBindingId}
                onChange={(e) => onSelect(e.target.value)}
                className="form-control form-control-width"
            >
                {bindings.length === 0 ? <option value="">No available option</option> : null}
                {bindings.map((binding) => (
                    <option key={binding.id} value={binding.id}>
                        {binding.developerName}
                    </option>
                ))}
            </select>
        </FormGroup>
    );
};

export default TypeBindingDropdown;
